import { useMemo, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { useGetDeliveryZonesQuery, useAssignSitesToDeliveryZoneMutation, TotadminPermission } from 'data/__generated__';
import { useHasPermission } from 'hooks/useHasPermission';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';

export function AssignSitesToZonePopup({
    isOpen,
    setIsOpen,
    siteIds,
    onAssigned,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    siteIds: string[];
    onAssigned: () => void;
}) {
    const [deliveryZoneId, setDeliveryZoneId] = useState<string | null>(null);

    // La popup est montée avec la liste des sites : sans le droit, son bouton d'ouverture est verrouillé
    // et la requête des zones ne part pas.
    const canReadDeliveryZones = useHasPermission(TotadminPermission.DeliveryZonesRead);
    const { data: zonesData } = useGetDeliveryZonesQuery({ skip: !canReadDeliveryZones });
    const [assignSitesToDeliveryZone, { loading }] = useAssignSitesToDeliveryZoneMutation();

    const zoneOptions = useMemo(
        () => (zonesData?.deliveryZones ?? []).map(({ _id, name }) => ({ value: _id, label: name })),
        [zonesData],
    );

    const handleSubmit = async () => {
        try {
            await assignSitesToDeliveryZone({ variables: { deliveryZoneId, siteIds } });
            toast.success(`${siteIds.length} site(s) affecté(s)`);
            setIsOpen(false);
            setDeliveryZoneId(null);
            onAssigned();
        } catch (error) {
            toast.error('Une erreur est survenue lors de l’affectation des sites');
        }
    };

    return (
        <TotemPopup
            title={`Affecter ${siteIds.length} site(s) à une zone`}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contentOverflow="visible"
        >
            <Fields>
                <TotemSelect
                    label="Zone de livraison"
                    placeholder="Sélectionner une zone (vide = retirer la zone)"
                    isClearable
                    value={deliveryZoneId}
                    options={zoneOptions}
                    onChange={setDeliveryZoneId}
                />
                <TotemPrimaryLoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    useIcon={false}
                    label="Confirmer"
                    requiredPermissions={[TotadminPermission.DeliveryZonesManage]}
                />
            </Fields>
        </TotemPopup>
    );
}

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
